import { NgIf } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TuiAppearance, TuiButton, TuiDialogContext, TuiLink, TuiNotification } from '@taiga-ui/core';
import { TuiCheckbox, TuiDataListWrapperComponent, tuiItemsHandlersProvider } from '@taiga-ui/kit';
import { TuiCardLarge } from '@taiga-ui/layout';
import { TuiInputModule, TuiSelectModule } from '@taiga-ui/legacy';
import { SignaturePad, SignaturePadModule } from '@viablelogic/ngx-signature-pad';
import { UserService } from '../services/user.service';
import { User } from '../models/user';
import { Qualification } from '../models/qualifications';
import { AuthService } from '../services/auth.service';
import { AuthOrdoclicService } from '../services/ordoclic/auth.service';
import { CommonService } from '../services/common.service';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-ordoclic-account-form-modal',
  standalone: true,
  imports: [
    TuiInputModule,
    TuiButton,
    ReactiveFormsModule,
    SignaturePadModule,
    TuiCardLarge,
    TuiAppearance,
    TuiCheckbox,
    TuiLink,
    NgIf,
    TuiNotification,
    TuiDataListWrapperComponent,
    TuiSelectModule,
  ],
  templateUrl: './ordoclic-account-form-modal.component.html',
  styleUrl: './ordoclic-account-form-modal.component.scss',
  providers: [
    tuiItemsHandlersProvider({
      stringify: (item: Qualification) => `${item.name}`,
    }),
  ],
})
export class OrdoclicAccountFormModalComponent {

  private route = inject(ActivatedRoute);
  errorMessage: string | null = null;
  private readonly userService = inject(UserService);

  readonly createProfessionalForm: FormGroup;
  user: User | null = null;

  @ViewChild(SignaturePad) signaturePad: SignaturePad | undefined;

  signaturePadOptions: Object = { // passed through to szimek/signature_pad constructor
    'minWidth': 1,
    'canvasWidth': 500,
    'canvasHeight': 200
  };

  //private readonly context: TuiDialogContext<boolean>;

  qualifications: Qualification[] = [];

  constructor(private authService: AuthService,
    private authOrdoclicService: AuthOrdoclicService,
    private fb: FormBuilder,
    private common: CommonService,
  ) {

    // @ts-ignore
    //this.context = inject(POLYMORPHEUS_CONTEXT);

    this.createProfessionalForm = this.fb.group({
      lastName: ['', [Validators.required]],
      firstName: ['', [Validators.required]],
      rpps: ['', [Validators.required]],
      qualification: [null, [Validators.required]],
      signature: ['', [Validators.required]],
      accept: [false, [Validators.requiredTrue]],
    });

    this.user = this.authService.getUser();

    this.route.params.subscribe((params) => {
      this.userService.pullDetail(this.user?.id)
        .pipe(
          map((temp: User) => {
            this.createProfessionalForm.patchValue({
              lastName: temp.nom || '',
              firstName: temp.prenom || '',
              rpps: temp.rpps || '',
            });
          })
        )
        .subscribe();
    });

  }

  ngOnInit() {
    this.userService.pullQualifications().subscribe(
      (qualifications: Qualification[]) => {
        this.qualifications = qualifications;
      }
    );
  }

  ngAfterViewInit() {
    // this.signaturePad is now available
    this.signaturePad?.set('minWidth', 5); // set szimek/signature_pad options at runtime
    this.signaturePad?.clear(); // invoke functions from szimek/signature_pad API
  }

  drawComplete() {
    // will be notified of szimek/signature_pad's onEnd event
    this.createProfessionalForm.patchValue({
      signature: this.signaturePad?.toDataURL()
    });
  }


  onSubmit() {
    if (this.createProfessionalForm.valid) {
      this.authOrdoclicService.generateSignature(environment.ordoclic.rpps_racine).subscribe({
        next: (response) => {
          const rawFormValue = this.createProfessionalForm.value

          const payload = {
            lastName: rawFormValue.lastName,
            firstName: rawFormValue.firstName,
            rpps: String(rawFormValue.rpps),
            qualification: rawFormValue.qualification,
          };

          this.authOrdoclicService.createProfesionnal(payload, this.signaturePad?.toDataURL()).subscribe({
            next: (response) => {
              if (this.authService.getToken()) {
                this.authService.verificationConnexion(this.authService.getToken()!).subscribe({
                  next: () => {
                    this.common.showSuccessAlert(
                      'Compte Ordoclic/Amelipro créé avec succès.',
                    );
                    //this.context.completeWith(true);
                  }
                });
              } else {
                this.common.showSuccessAlert(
                  'Compte Ordoclic/Amelipro créé avec succès. Veuillez vous reconnecter.',
                );
                //this.context.completeWith(true);
              }
            },
            error: (error) => {
              this.errorMessage = error.error?.message || 'Une erreur est survenue. Veuillez réessayer.';
            }
          });
        },
        error: (error) => {
          this.errorMessage = error.error?.message || 'Une erreur est survenue. Veuillez réessayer.';
        }
      });


    } else {
      console.log('Form is invalid');
    }

  }

}
